<script lang="ts">
	import {
		Alert,
		Button,
		Container,
		Input,
		Text,
		toast,
		Toggle,
	} from '@Shelly/shelly-design-system/index';
	import { getWorkingModeIcon } from 'src/helpers/device_data_helpers';
	import { celsius_to_fahrenheit, defaultTempFavPositions } from 'src/helpers/interface_helpers';
	import { capFirstChar, valueOutOfRange } from 'src/helpers/utilities';
	import { postReq } from 'src/services/http';
	import { temp_schemes } from 'src/stores/lists/scenes-derived';
	import { scenes } from 'src/stores/lists/scenes';
	import { format } from 'svelte-i18n';

	export let id;

	$: thermostat_json = $temp_schemes[id];
	let prevUnit = thermostat_json?.isinfh ? 'F' : 'C';
	$: unit = thermostat_json?.isinfh ? 'F' : 'C';
	$: min = unit === 'C' ? 5 : Math.round(celsius_to_fahrenheit(5));
	$: max = unit === 'C' ? 35 : Math.round(celsius_to_fahrenheit(35));
	$: temp_counter = thermostat_json?.temp_counter || 0;
	$: temp_fav_positions = JSON.parse(
		JSON.stringify(thermostat_json?.temp_fav_positions || defaultTempFavPositions(prevUnit)),
	);

	$: if (prevUnit !== unit) {
		temp_fav_positions = JSON.parse(
			JSON.stringify(thermostat_json?.temp_fav_positions || defaultTempFavPositions(unit)),
		);
		prevUnit = unit;
	}

	const saveTempFavPosition = async () => {
		const editedThermostat = {
			...thermostat_json,
			temp_fav_positions,
			temp_counter: temp_counter + 1,
		};
		const res = await postReq('/scene/edit', {
			id: thermostat_json._id,
			scene_script: JSON.stringify(editedThermostat),
		});
		if (res?.isok) {
			scenes.editScene(editedThermostat);
			toast.open({
				type: 'success',
				title: $format('toast_success_favorite_temperature_position', {
					default: 'Successfully saved favorite temperature position',
				}),
			});
		}
	};
</script>

<Container type="modalContainer" class="border-color-transparent" padding="xs">
	<Alert
		type="info"
		class="mb-s"
		heading={$format('alert_info_heading_unit_temp_fav_positions', {
			default:
				'Your device currently measures in {unitName} (°{unitValue}), so the values must be between {min} and {max}.',
			values: {
				unitName:
					unit === 'C'
						? $format('label_celsius', { default: 'Celsius' })
						: $format('label_fahrenheit', { default: 'Fahrenheit' }),
				unitValue: unit,
				min,
				max,
			},
		})}
	/>
	{#each temp_fav_positions as pos}
		<Text styleType="body-small" class="mb-xs">
			{$format(`temp_fav_position_${pos.mode}`, {
				default: capFirstChar(pos.mode),
			})}
		</Text>
		<div class="mb-s d-flex">
			<Input
				aria-label={$format('input_set_favorite_temperature', {
					default: 'Input set favorite temperature',
				})}
				size="m"
				class="mr-s"
				type="number"
				{min}
				{max}
				value={pos.value}
				disabled={!pos.active}
				error={valueOutOfRange(pos.value, min, max)
					? $format('min_max_error_description', {
							values: { min, max },
							default: 'Value must be between {min} and {max}.',
						})
					: undefined}
				icon={getWorkingModeIcon(pos.mode)}
				on:input={(e) => (pos.value = e.target.value)}
				id="input-x10be25"
			/>
			<Toggle
				aria-label={$format('toggle_activate_deactivate_favorite_temperature', {
					default: 'Toggle activate-deactivate favorite temperature',
				})}
				size="m"
				checked={pos.active}
				on:click={() => (pos.active = !pos.active)}
				id="toggle-hymobu9"
			/>
		</div>
	{/each}
	<Button
		disabled={temp_fav_positions.find((i) => valueOutOfRange(i.value, min, max) && i.active)}
		class="mt-s"
		size="m"
		width="full"
		on:click={saveTempFavPosition}
	>
		{$format('button_save')}
	</Button>
</Container>
